export interface IndustryNewsTag {
  title: string;
  slug: string;
}

export interface IndustryNews {
  selected?: boolean;
  id: string;
  date: string;
  last_modified: string;
  title: string;
  url: string;
  lat: number | undefined;
  lng: number | undefined;
  /** Tags supplied by the third-party API. Iran Conflict articles carry
   *  { title: "Iran Conflict", slug: "iran-conflict" } in this array.
   *  Individual elements may be null if the API returns sparse arrays. */
  tags?: (IndustryNewsTag | null)[];
  /** Content type supplied by the proxy endpoint.
   *  Optional — not all items carry a type. */
  type?: 'news' | 'articles';
}

/** Returns true when the article is tagged for the Iran Conflict region. */
export const isIranConflictTagged = (news: IndustryNews): boolean =>
  news.tags?.some((tag) => tag != null && tag.slug === 'iran-conflict') ??
  false;

/**
 * Returns true if the news item should be shown given an active focus region.
 * Filters by start date, then by polygon containment or iran-conflict tag.
 * Used by both the panel list and the map pin layer to keep them in sync.
 */
export const newsMatchesFocusRegion = (
  news: IndustryNews,
  polygon: GeoJSON.Polygon,
  startDate: string | null,
  booleanPointInPolygon: (
    point: GeoJSON.Feature<GeoJSON.Point>,
    polygon: GeoJSON.Feature<GeoJSON.Polygon> | GeoJSON.Polygon
  ) => boolean,
  turfPoint: (coords: [number, number]) => GeoJSON.Feature<GeoJSON.Point>
): boolean => {
  if (startDate && new Date(news.date) < new Date(startDate)) return false;
  if (isIranConflictTagged(news)) return true;
  if (
    news.lat == null ||
    news.lng == null ||
    !Number.isFinite(news.lat) ||
    !Number.isFinite(news.lng)
  )
    return false;
  return booleanPointInPolygon(turfPoint([news.lng, news.lat]), polygon);
};

/** Maps the API type value (plural lowercase) to a human-readable display label.
 *  Falls back to capitalising the first letter for unknown values. */
const NEWS_TYPE_LABELS: Record<string, string> = {
  articles: 'Article',
  news: 'Industry News',
};

export const formatNewsType = (type: string): string =>
  NEWS_TYPE_LABELS[type.toLowerCase()] ??
  type.charAt(0).toUpperCase() + type.slice(1);

export interface IndustryNewsResponse {
  data: IndustryNews[];
  last_page: number;
}

export type IndustryNewsFeature = GeoJSON.Feature<GeoJSON.Point, IndustryNews>;
